import { getMainPageRestrictionRedirect, hasMainPageRestriction } from '../../../services/accessManagement/mainPageRestriction';
import type { BackendUser } from '../../../services/user/userService';
import {
  isInvoiceOnlyAccount, canAccessChecklistsPage, canAccessCostPercentPage,
  canAccessInventoryPage, canAccessMepPage, canAccessNotificationsPage,
  canAccessRecipesPage, canAccessStaffPage, canAccessWastePage,
} from '../../../services/accessManagement/accessManagement';

type Access = (user: BackendUser | null, settings: string[]) => boolean;
export interface SupportGuide {
  id: string;
  path: string;
  group: 'overview' | 'appPages' | 'account';
  tourId?: string;
  access?: Access;
  invoiceOnly?: 'only' | 'also';
  withoutSubscription?: boolean;
  /** Route family coverage; detail IDs are never constructed from untrusted content. */
  routes: string[];
}
const admin: Access = user => user?.role === 'admin';

export const SUPPORT_GUIDES: readonly SupportGuide[] = [
  { id: 'dashboard', path: '/dashboard', group: 'overview', tourId: 'dashboard', routes: ['/dashboard', '/dashboard/restaurants'] },
  { id: 'orders', path: '/orderhistory', group: 'overview', tourId: 'orders', routes: ['/orderhistory', '/orderhistory/products', '/orderhistory/orders', '/orderhistory/:orderId', '/orderhistory/factuur/:factuurId'] },
  { id: 'invoiceOrders', path: '/orderhistory/orders', group: 'overview', invoiceOnly: 'only', routes: ['/orderhistory/orders', '/orderhistory/:orderId', '/orderhistory/factuur/:factuurId'] },
  { id: 'revenue', path: '/revenuehistory', group: 'overview', tourId: 'revenue', routes: ['/revenuehistory', '/revenuehistory/items', '/revenuehistory/items/:itemName', '/revenuehistory/revenue', '/revenuehistory/detail/:revenueId'] },
  { id: 'costPercent', path: '/costpercent', group: 'overview', access: canAccessCostPercentPage, routes: ['/costpercent', '/costpercent/waste'] },
  { id: 'priceAlerts', path: '/pricealerts', group: 'overview', routes: ['/pricealerts'] },
  { id: 'costPrice', path: '/costprice', group: 'overview', tourId: 'costPrice', routes: ['/costprice', '/costprice/create', '/costprice/manage-ingredients', '/costprice/subrecipe-review', '/costprice/automation-review/:runId', '/costprice/automation-review/:runId/category/:categoryId', '/costprice/:id/edit'] },
  { id: 'utilization', path: '/utilization', group: 'overview', tourId: 'utilization', routes: ['/utilization'] },
  { id: 'menus', path: '/menus', group: 'overview', routes: ['/menus'] },
  { id: 'charts', path: '/charts', group: 'overview', routes: ['/charts'] },
  { id: 'recipes', path: '/recipes', group: 'appPages', access: canAccessRecipesPage, routes: ['/recipes', '/recipes/:id'] },
  { id: 'checklists', path: '/checklists', group: 'appPages', access: canAccessChecklistsPage, routes: ['/checklists'] },
  { id: 'mep', path: '/mep', group: 'appPages', access: canAccessMepPage, routes: ['/mep', '/mep/:sectionId'] },
  { id: 'inventory', path: '/inventory?tab=review', tourId: 'inventory', group: 'appPages', access: canAccessInventoryPage, routes: ['/inventory', '/inventory/edit', '/inventory/section/:sectionId', '/inventory/all-products'] },
  { id: 'leakage', path: '/inventory?tab=variance', tourId: 'leakage', group: 'appPages', access: canAccessInventoryPage, routes: ['/inventory?tab=variance'] },
  { id: 'notifications', path: '/notifications', group: 'appPages', access: canAccessNotificationsPage, routes: ['/notifications'] },
  { id: 'waste', path: '/waste', group: 'appPages', access: canAccessWastePage, routes: ['/waste'] },
  { id: 'staff', path: '/staff', group: 'account', access: canAccessStaffPage, routes: ['/staff'] },
  { id: 'profile', path: '/profile', group: 'account', invoiceOnly: 'also', routes: ['/profile', '/restaurant/:restaurantId', '/profile/add-data-source/:restaurantId'] },
  { id: 'userProfile', path: '/profile', group: 'account', invoiceOnly: 'also', routes: ['/profile'] },
  { id: 'settings', path: '/settings', group: 'account', access: admin, invoiceOnly: 'also', routes: ['/settings', '/settings/legal/:docKey'] },
  { id: 'subscription', path: '/settings', group: 'account', invoiceOnly: 'also', access: admin, routes: ['/settings'] },
  { id: 'support', path: '/support', group: 'account', invoiceOnly: 'also', withoutSubscription: true, routes: ['/support', '/support/guides', '/support/guides/:guideId', '/support/faq'] },
  { id: 'home', path: '/home', group: 'overview', invoiceOnly: 'only', routes: ['/home'] },
  { id: 'invoices', path: '/invoices', group: 'overview', invoiceOnly: 'only', withoutSubscription: true, routes: ['/invoices'] },
];

export const getSupportGuide = (id: string | undefined): SupportGuide | undefined => SUPPORT_GUIDES.find(guide => guide.id === id);

export function getAccessibleGuides(user: BackendUser | null, settings: string[], subscriptionActive: boolean): SupportGuide[] {
  if (!user || (user.role === 'staff' && ['pending', 'rejected'].includes(user.access_request ?? ''))) return [];
  const invoiceOnly = !hasMainPageRestriction(user) && isInvoiceOnlyAccount(user, settings);
  return SUPPORT_GUIDES.filter(guide =>
    !getMainPageRestrictionRedirect(guide.path.split('?')[0], user, settings) &&
    (subscriptionActive || guide.withoutSubscription) &&
    (invoiceOnly ? Boolean(guide.invoiceOnly) : guide.invoiceOnly !== 'only') &&
    (!guide.access || guide.access(user, settings)));
}

export const GUIDE_SECTIONS = ['where', 'what', 'important'] as const;

export interface SupportGuideArticle {
  title: string;
  summary: string;
  sections: {
    where: string;
    what: string[];
    important: { intro: string; items: string[] };
  };
}

export function getGuideReadingMinutes(article: SupportGuideArticle): number {
  const text = [article.title, article.summary, article.sections.where, ...article.sections.what,
    article.sections.important.intro, ...article.sections.important.items].join(' ').replace(/\*\*/g, '');
  return Math.min(5, Math.max(1, Math.ceil(text.trim().split(/\s+/).length / 200)));
}

export const SUPPORT_FAQ_TOPICS = ['gettingStarted', 'data', 'account'] as const;

/** FAQ navigation stays typed and permission checked, with no URLs in translated prose. */
export const SUPPORT_FAQ_ENTRIES = [
  { id: 'pages', topic: 'gettingStarted', guideId: 'dashboard', section: 'what' },
  { id: 'purchases', topic: 'data', guideId: 'orders', section: 'what' },
  { id: 'revenue', topic: 'data', guideId: 'revenue', section: 'what' },
  { id: 'staff', topic: 'account', guideId: 'staff', section: 'important' },
  { id: 'access', topic: 'account', guideId: 'support', section: 'important' },
  { id: 'inventory', topic: 'data', guideId: 'inventory', section: 'what' },
  { id: 'missingData', topic: 'data', guideId: 'support', section: 'important' },
  { id: 'tours', topic: 'gettingStarted', guideId: 'support', section: 'what' },
  { id: 'feedback', topic: 'gettingStarted', guideId: 'support', section: 'important' },
  { id: 'manualUpload', topic: 'data', guideId: 'invoices', section: 'important' },
  { id: 'leakage', topic: 'data', guideId: 'leakage', section: 'important' },
  { id: 'recipes', topic: 'data', guideId: 'recipes', section: 'what' },
  { id: 'contactSupport', topic: 'gettingStarted', guideId: 'support', section: 'important' },
] as const;

/** Gate the answer itself, not just its link, using the destination's actual access rules. */
export function getAccessibleFaqEntries(user: BackendUser | null, settings: string[], subscriptionActive: boolean) {
  const guides = getAccessibleGuides(user, settings, subscriptionActive);
  return SUPPORT_FAQ_ENTRIES.flatMap(entry => {
    const guide = guides.find(candidate => candidate.id === entry.guideId ||
      (entry.guideId === 'orders' && candidate.id === 'invoiceOrders'));
    if (!guide || (entry.id === 'tours' && (!subscriptionActive ||
      !user || (!hasMainPageRestriction(user) && isInvoiceOnlyAccount(user, settings))))) return [];
    return [{ ...entry, guide }];
  });
}
